import React, { useState, useEffect, useMemo } from "react";
import * as Yup from "yup";
import ApiServices from "../../Services/ApiServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFileAlt,
} from "@fortawesome/free-solid-svg-icons";
import SoftGrid from "../../Shared/SoftGrid/SoftGrid";
import { toastHelper } from "../../../Helpers/toastHelper";
import DeleteAlert from "../../Shared/DeleteAlert";
import Loader from "../../Shared/Loader";
import extractErrorMessage from "../../../Helpers/APIHelper";
import DynamicModal from "../../Shared/DynamicModal";
import { useLanguage } from '../../../contexts/LanguageContext';
import htmlHelper from '../../../Helpers/htmlHelper';


const EMPTY_BLOG_FORM = {
  category_id: "",
  title: "",
  description: "",
  content: "",
  image: null,
};

const BlogManager = () => {
  const { t } = useLanguage();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddEdit, setShowAddEdit] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [blogFormInitialData, setBlogFormInitialData] = useState(EMPTY_BLOG_FORM);
  const [categories, setCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [blogModalLoading, setBlogModalLoading] = useState(false);

  const fetchBlogs = async (page, searchWord = '') => {
    setLoading(true);
    try {
      const response = await ApiServices.get(
        `Blog/getAll?PageSize=${pageSize}&PageNumber=${page}&IsActive=true&Word=${encodeURIComponent(searchWord)}`
      );
      if (response.check) {
        setBlogs(response.data.items);
        setTotalPages(response.data.total_pages);
      }
    } catch (error) {
      toastHelper.error(t('blogLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs(currentPage, searchQuery);
  }, [currentPage, searchQuery, pageSize]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSearch = (term) => {
    setSearchQuery(term);
    setCurrentPage(1);
  };

  const handleCloseForm = () => {
    setShowAddEdit(false);
    setEditingBlog(null);
    setBlogFormInitialData(EMPTY_BLOG_FORM);
    fetchBlogs(currentPage, searchQuery);
  };

  const loadCategories = async () => {
    if (categoriesLoaded) return;
    const response = await ApiServices.get("Lookup/Categories");
    if (Array.isArray(response)) {
      setCategories(response);
      setCategoriesLoaded(true);
    }
  };

  const openCreateBlogModal = async () => {
    try {
      setBlogModalLoading(true);
      await loadCategories();
      setEditingBlog(null);
      setBlogFormInitialData(EMPTY_BLOG_FORM);
      setShowAddEdit(true);
    } catch (error) {
      toastHelper.error(t('categoriesLoadFailed'));
    } finally {
      setBlogModalLoading(false);
    }
  };

  const openEditBlogModal = async (blog) => {
    try {
      setBlogModalLoading(true);
      await loadCategories();
      const response = await ApiServices.get(`Blog/${blog.id}`);
      const details = response?.data || response || blog;
      setEditingBlog(details);
      setBlogFormInitialData({
        category_id: details?.category_id ? String(details.category_id) : "",
        title: details?.title || "",
        description: details?.description || "",
        content: details?.content || "",
        image: null,
      });
      setShowAddEdit(true);
    } catch (error) {
      toastHelper.error(t('blogDetailsLoadFailed'));
    } finally {
      setBlogModalLoading(false);
    }
  };

  const blogFormConfig = useMemo(() => [
    {
      name: "category_id",
      label: t('blogCategory'),
      type: "select",
      required: true,
      requiredMessage: t('blogCategoryRequired'),
      options: categories.map((cat) => ({ value: String(cat.id), label: cat.name })),
      defaultValue: blogFormInitialData.category_id,
    },
    {
      name: "title",
      label: t('blogTitle'),
      type: "text",
      required: true,
      defaultValue: blogFormInitialData.title,
      validation: Yup.string()
        .required(t('blogTitleRequired'))
        .min(3, t('blogTitleMinError'))
        .max(100, t('blogTitleMaxError')),
    },
    {
      name: "description",
      label: t('blogShortDescription'),
      type: "styledText",
      height: 200,
      required: true,
      defaultValue: blogFormInitialData.description,
      validation: Yup.string()
        .required(t('blogDescriptionRequired'))
        .min(10, t('blogDescMinError')),
    },
    {
      name: "content",
      label: t('content'),
      type: "styledText",
      height: 350,
      required: true,
      defaultValue: blogFormInitialData.content,
      validation: Yup.string()
        .required(t('blogContentRequired'))
        .min(20, t('blogContentMinError')),
    },
    {
      name: "image",
      label: t('blogImage'),
      type: "file",
      accept: "image/*",
      defaultValue: null,
    },
  ], [categories, blogFormInitialData, t]);

  const handleBlogModalSubmit = async (values) => {
    const formData = new FormData();
    formData.append("CategoryId", values.category_id);
    formData.append("Title", values.title);
    formData.append("Content", values.content);
    formData.append("Description", values.description);
    if (values.image instanceof File) {
      formData.append("BlogImage", values.image, values.image.name);
    }

    try {
      if (editingBlog?.id) {
        const response = await ApiServices.put(`Blog/update/${editingBlog.id}`, formData, true);
        if (!response?.check) throw new Error(t('blogUpdateFailed'));
        toastHelper.success(t('blogUpdatedSuccess'));
      } else {
        const response = await ApiServices.post("Blog/create", formData, true);
        if (!response?.check) throw new Error(t('blogCreateFailed'));
        toastHelper.success(t('blogCreatedSuccess'));
      }

      handleCloseForm();
    } catch (error) {
      const title = error?.title || error?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        toastHelper.error(extractErrorMessage(error));
      } else {
        toastHelper.error(error?.msg || error?.message || t('blogSaveFailed'));
      }
      throw error;
    }
  };

  const handleDelete = (blog) => {
    setSelectedBlog(blog);
    setShowDeleteAlert(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBlog) return;

    setDeleteLoading(true);
    try {
      const response = await ApiServices.delete(`Blog/delete/${selectedBlog.id}`);
      if (response.check) {
        toastHelper.success(t('blogDeletedSuccess'));
        fetchBlogs(currentPage, searchQuery);
      }
    } catch (error) {
      toastHelper.error(t('blogDeleteFailed'));
    } finally {
      setDeleteLoading(false);
      setShowDeleteAlert(false);
      setSelectedBlog(null);
    }
  };

  const filteredAndSortedBlogs = useMemo(() => {
    return [...blogs];
  }, [blogs]);

  return (
    loading ? (
      <Loader />
    ) : (
      <div className="blog-manager">
        <div className="section-header">
          <div className="header-left">
            <h2>{t('blogManagement')}</h2>
          </div>
          <div className="header-right">
            <button
              className="add-blog-btn"
              onClick={openCreateBlogModal}
              disabled={blogModalLoading}
            >
              <FontAwesomeIcon icon={faPlus} />
              {blogModalLoading ? t('loading') : t('addNewBlog')}
            </button>
          </div>
        </div>

        <DynamicModal
          isOpen={showAddEdit}
          onClose={handleCloseForm}
          title={editingBlog?.id ? t('editBlog') : t('createNewBlog')}
          submitButtonText={editingBlog?.id ? t('updateBlog') : t('saveBlog')}
          formConfig={blogFormConfig}
          onSubmit={handleBlogModalSubmit}
          onError={() => { }}
        />

        <DeleteAlert
          isOpen={showDeleteAlert}
          onClose={() => {
            setShowDeleteAlert(false);
            setSelectedBlog(null);
          }}
          onConfirm={handleDeleteConfirm}
          title={t('confirmDeleteBlogTitle')}
          message={t('confirmDeleteBlog')}
          itemName={selectedBlog?.title}
          itemDescription={`${t('categoryPrefix')}${selectedBlog?.category_name}`}
          loading={deleteLoading}
          confirmText={t('delete')}
          cancelText={t('cancel')}
        />

        <SoftGrid
          columns={[
            {
              key: 'title',
              label: t('title'),
              sortable: true,
              render: (value, row) => (
                <div className="blog-title-cell">
                  <FontAwesomeIcon icon={faFileAlt} />
                  <span>{value}</span>
                </div>
              ),
            },
            {
              key: 'description',
              label: t('addEditMozakaraDescriptionLabel'),
              sortable: true,
              render: (value) => htmlHelper.stripHtml(value),
            },
            {
              key: 'category_name',
              label: t('blogCategory'),
              sortable: true,
            },
          ]}
          data={filteredAndSortedBlogs}
          actions={[
            (row) => {
              const actions = [
                {
                  tooltip: t('edit'),
                  icon: <FontAwesomeIcon icon={faEdit} />,
                  onClick: () => openEditBlogModal(row),
                },
                {
                  tooltip: t('delete'),
                  icon: <FontAwesomeIcon icon={faTrash} />,
                  onClick: () => handleDelete(row),
                  className: 'delete',
                },
              ];

              return actions.map((action) => ({
                ...action,
                label: action.label || action.tooltip,
              }));
            },
          ]}
          isLoading={loading}
          isPaginated={true}
          pageNumber={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onPageSizeChange={(newSize) => {
            setPageSize(newPageSize => {
              if (newPageSize !== newSize) {
                setCurrentPage(1);
                return newSize;
              }
              return newPageSize;
            });
          }}
          searchPlaceholder={t('searchBlogPlaceholder')}
          columnResizable={true}
          onSearch={handleSearch}
          exportFilename="blogs-export"
          isColumnDraggable={true}
          isColumnSelectable={true}
          emptyStateMessage={t('noBlogsAvailable')}
        />

        <style jsx>{`
          .blog-manager {
            padding: 24px;
            background: #fff;
            min-height: calc(100vh - 48px);
            width: 100%;
            overflow-x: hidden;
            border-radius: 30px;
          }


        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          padding: 24px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(8px);
        }


          .header-left {
            display: flex;
            align-items: center;
            gap: 24px;
            flex: 1;
            min-width: 300px;
          }

          .header-left h2 {
            font-size: 24px;
            font-weight: 600;
            color: #1a1f36;
            margin: 0;
              font-family: "Zain", sans-serif;
          }

          .header-right {
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
          }

          .add-blog-btn {
            padding: 12px 24px;
            background: #6366f1;
            color: white;
            border: none;
            border-radius: 12px;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: all 0.3s ease;
            font-size: 14px;
            font-weight: 500;
              font-family: "Zain", sans-serif;
          }

          .add-blog-btn:hover {
            background: #357abd;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(74, 144, 226, 0.2);
          }

          .add-blog-btn svg {
            width: 16px;
            height: 16px;
          }

          .blog-title-cell {
            display: flex;
            align-items: center;
            gap: 8px;
            color: #1a1f36;
            font-weight: 500;
          }

          .blog-title-cell svg {
            color: #4a90e2;
            font-size: 14px;
          }

          /* AddEditBlog form styles */
          .add-edit-blog-form {
            background: #e4e4f1;
            border-radius: 24px;
            max-width: 600px;
            margin: 2rem auto;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
            display: flex;
            flex-direction: column;
          }
          .form-header {
            padding: 2rem;
            border-bottom: 2px solid #e2e8f0;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: #e4e4f1;
            border-top-left-radius: 24px;
            border-top-right-radius: 24px;
          }
          .form-header h2 {
            font-size: 1.5rem;
            font-weight: 700;
            background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            margin: 0;
          }
          .close-btn {
            background: none;
            border: none;
            color: #64748b;
            cursor: pointer;
            padding: 0.75rem;
            border-radius: 12px;
            transition: all 0.3s;
            min-width: 44px;
            min-height: 44px;
          }
          .close-btn:hover {
            background: #f1f5f9;
            color: #1e293b;
          }
          .form-content {
            padding: 2rem;
            overflow-y: auto;
            flex-grow: 1;
          }

          /* Grid system for form layout */
          .row {
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            gap: 24px;
            margin-bottom: 24px;
          }
          .col-12 {
            flex-basis: 100%;
            max-width: 100%;
          }
          .input-group {
            display: flex;
            flex-direction: column;
            gap: 8px;
            background: #fff;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 18px 16px 10px 16px;
            box-shadow: 0 1px 4px 0 rgba(34, 197, 94, 0.04);
          }
          .input {
            width: 100%;
            padding: 10px 12px;
            font-size: 17px;
              font-family: "Zain", sans-serif;
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            outline: none;
            transition: border-color 0.2s, box-shadow 0.2s;
          }
          .input:focus {
            border-color: #6366f1;
            background: #f0fdf4;
          }
          .input-error {
            border-color: #e11d48 !important;
            background: #fef2f2 !important;
          }
          .error-text {
            color: #e11d48;
            font-size: 14px;
            margin-top: 2px;
          }

          .image-upload {
            display: flex;
            gap: 2rem;
            align-items: flex-start;
          }
          .image-preview {
            width: 120px;
            height: 120px;
            border-radius: 16px;
            overflow: hidden;
            background: #f8fafc;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px dashed #e2e8f0;
          }
          .image-preview img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .image-placeholder {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            color: #64748b;
          }
          .image-placeholder svg {
            font-size: 32px;
          }
          .image-input {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          .image-input input[type="file"] {
            display: none;
          }
          .upload-btn {
            background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
            color: white;
            border: none;
            padding: 1rem 2rem;
            border-radius: 16px;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 0.75rem;
            transition: all 0.3s;
            box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2);
          }
          .upload-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(79, 70, 229, 0.3);
          }
          .form-actions {
            padding: 2rem 0 0 0;
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
          }
          .cancel-btn,
          .submit-btn {
            padding: 1rem 2rem;
            border-radius: 16px;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 0.75rem;
            transition: all 0.3s;
          }
          .cancel-btn {
            background: #f1f5f9;
            color: #2d3748;
            border: none;
          }
          .submit-btn {
            background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
            color: white;
            border: none;
            box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2);
          }
          .submit-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }
          .error-message {
            color: #dc2626;
            font-size: 0.875rem;
            margin-top: 0.5rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          .error-message::before {
            content: "!";
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            background: #fee2e2;
            color: #dc2626;
            border-radius: 50%;
            font-size: 0.75rem;
            font-weight: bold;
          }

          .main-container {
            min-height: 100vh;
            width: 100%;
            background: #e9e8eb;
              font-family: "Zain", sans-serif;
            padding: 0;
            margin: 0;
          }
          .row {
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            gap: 24px;
            margin-bottom: 24px;
          }
          .col-6 {
            flex: 1 1 48%;
            max-width: 48%;
          }
          .col-12 {
            flex-basis: 100%;
            max-width: 100%;
          }
          .input-group {
            display: flex;
            flex-direction: column;
            gap: 8px;
            background: #fff;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 18px 16px 10px 16px;
            box-shadow: 0 1px 4px 0 rgba(34, 197, 94, 0.04);
          }
          .input {
            width: 100%;
            padding: 10px 12px;
            font-size: 17px;
              font-family: "Zain", sans-serif;
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            outline: none;
            transition: border-color 0.2s, box-shadow 0.2s;
          }
          .input:focus {
            border-color: #6366f1;
            background: #f0fdf4;
          }
          .input-error {
            border-color: #e11d48 !important;
            background: #fef2f2 !important;
          }
          .error-text {
            color: #e11d48;
            font-size: 14px;
            margin-top: 2px;
          }
          .actions-row {
            flex-basis: 100%;
            max-width: 100%;
            display: flex;
            gap: 16px;
            margin-top: 24px;
          }
          .cancel-btn {
            background: #f3f4f6;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.7rem 1.4rem;
            margin-right: 20px;
            border-radius: 0.6rem;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            border: none;
            transition: background 0.18s, color 0.18s, box-shadow 0.18s,
              border 0.18s;
            outline: none;
            font-family: inherit;
          }
          .cancel-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }
          .cancel-btn:hover {
            background: #e0e7ef;
          }
          .cancel-btn:focus {
            box-shadow: 0 0 0 2px #6366f1;
          }
          .cancel-btn:blur {
            box-shadow: none;
          }
          .submit-btn {
            background: linear-gradient(90deg, #6366f1 0%, #3b82f6 100%);
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.7rem 1.4rem;
            margin-right: 20px;
            border-radius: 0.6rem;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            border: none;
            transition: background 0.18s, color 0.18s, box-shadow 0.18s,
              border 0.18s;
            outline: none;
            font-family: inherit;
          }
          .submit-btn.saving {
            opacity: 0.7;
            cursor: not-allowed;
          }
          .submit-btn:hover {
            background: linear-gradient(90deg, #16a34a 0%, #6366f1 100%);
          }
          .submit-btn:focus {
            box-shadow: 0 0 0 2px #6366f1;
          }
          .submit-btn:blur {
            box-shadow: 0 2px 12px 0 rgba(34, 197, 94, 0.13),
              0 1.5px 8px 0 rgba(22, 163, 74, 0.1);
          }

          @media (max-width: 900px) {
            .section-header {
              flex-direction: column;
              gap: 1rem;
              padding: 1.5rem;
            }
            .header-left {
              width: 100%;
              flex-direction: column;
              gap: 1rem;
            }
            .header-right {
              width: 100%;
            }
            .add-blog-btn {
              width: 100%;
              justify-content: center;
            }
            .row {
              flex-direction: column;
              gap: 18px;
            }
            .col-6 {
              max-width: 100%;
              flex-basis: 100%;
            }
          }
          @media (max-width: 600px) {
            .blog-manager {
              padding: 0.75rem;
            }
            .section-header {
              padding: 1rem;
            }
            .header-left h2 {
              font-size: 1.25rem;
            }
            .add-edit-blog-form {
              max-width: 100%;
              border-radius: 12px;
            }
            .form-header,
            .form-content {
              padding: 0.75rem;
            }
            .row {
              gap: 12px;
              margin-bottom: 12px;
            }
            .input-group {
              padding: 12px 8px 8px 8px;
            }
            .form-group {
              margin-bottom: 1.5rem;
              width: 100%;
              display: flex;
              flex-direction: column;
            }
            .form-group input,
            .form-group select,
            .form-group textarea {
              border-radius: 8px;
              font-size: 0.95rem;
              padding: 0.75rem 1rem;
              width: 100%;
              box-sizing: border-box;
              margin-bottom: 0.5rem;
            }
            .rich-text-editor {
              width: 100%;
              min-width: 0;
              margin-bottom: 0.5rem;
            }
            .form-actions {
              gap: 0.5rem;
              flex-direction: column;
              align-items: stretch;
            }
            .add-edit-blog-form .image-upload {
              flex-direction: column !important;
              gap: 1rem !important;
              align-items: stretch !important;
            }
            .add-edit-blog-form .image-preview {
              margin-bottom: 0.5rem;
            }
          }
        `}</style>
      </div>
    )
  );
};

export default BlogManager;
